import { useState } from "react";
import { Button, Loader } from "semantic-ui-react";
import { getStoreId, WhatIfScenarioConfig, WhatIfScenarioInputValues, WhatIfScenarioRecord, WhatIfScenarioValuesPerProject } from "../../ps-types";
import { getSalesforceConfigKeys, getSalesforceValues } from "./whatIfScenarios.client";
import { useCompanyId } from "../../core";
import { clone, mergeDeepRight, values } from "ramda";
import { useMessages } from "../../ui/MessagesProvider";
import { buildLineItemsStorePersistence } from "../../lineitems-store/RestLineItemsStore.persistence";
import { authStorage } from "../../auth";
import { convertUnixTimeToExcelNumberTime, getAmProjectConfig } from "../../ps-models";
import { CANONICAL_NAME_FIELD, pQuery, StoreQuery } from "../../ps-models/lineitems-store";
import { differenceInDays, differenceInMonths, differenceInYears } from "date-fns";


interface LoadFromDataSourceBtnGlobalInputsProps extends LoadFromDataSourceBtnBaseProps {
  variant: "global-inputs",
  onDataLoaded: (updatedValues: WhatIfScenarioInputValues) => void,
}

interface LoadFromDataSourceBtnProjectVersionInputsProps extends LoadFromDataSourceBtnBaseProps {
  variant: "project-version-inputs",
  onDataLoaded: (updatedValues: WhatIfScenarioValuesPerProject) => void,
}

interface LoadFromDataSourceBtnBaseProps {
  config: WhatIfScenarioConfig,
  projectVersions: WhatIfScenarioRecord['inputData']['projectVersions']
}


export function LoadfromDataSourceBtn(props: LoadFromDataSourceBtnGlobalInputsProps | LoadFromDataSourceBtnProjectVersionInputsProps) {
  const { config, projectVersions, variant, onDataLoaded } = props;
  const companyId = useCompanyId();
  const company = authStorage.getCompany();
  let { collection, namespace } = getAmProjectConfig(company);
  const [loadingFromDataSource, setLoadingFromDataSource] = useState(false);
  const [dataSourceLoadingText, setDataSourceLoadingText] = useState("Load from Data Source");
  const { info, error } = useMessages();


  const loadSaleforceConfig = async (salesforceConfigKey: string) => {
    let projectVersionInputs: WhatIfScenarioValuesPerProject = {};
    try {
      let salesforceValues = await getSalesforceValues(companyId, salesforceConfigKey);

      console.info("Salesforce Values", salesforceValues)

      // Update the project version inputs

      for (let pv of projectVersions) {
        const key = pv.projectId + "-" + pv.versionId;

        const salesforceValuesForProject = values(salesforceValues[pv.projectId]);

        if (!salesforceValuesForProject) {
          error(`No Salesforce values found for project ${pv.projectId}`);
          continue;
        }

        for (let salesforceValue of salesforceValuesForProject) {

          if (!projectVersionInputs[key]) {
            projectVersionInputs[key] = {};
          }

          projectVersionInputs[key][salesforceValue.valueKey] = {
            valueKey: salesforceValue.valueKey,
            value: salesforceValue.value
          }
        }

      }
      console.info("Updated Project Version Inputs", projectVersionInputs)
    } catch (e: any) {
      error(`Error calling  Salesforce loader ${salesforceConfigKey} ${e.message}`);
    }
    return projectVersionInputs;
  }

  const loadFromQueryDataSource = async () => {
    let inputs: WhatIfScenarioValuesPerProject | WhatIfScenarioInputValues = {};
    try {
      let lineItemNamesMapping: Record<string, string> = {};
      Object.values(config.mappingConfig).forEach((it) => {
        if (it.dataSource?.type === 'query') {
          lineItemNamesMapping[it.valueKey] = it.dataSource.lineItemName;
        }
      }); 
      let persistence = buildLineItemsStorePersistence();
      const store = await persistence.query(collection, pQuery()
        .withStoreIds(projectVersions.map((it) => getStoreId(it.projectId, it.versionId)))
        .withLineItems(Object.values(lineItemNamesMapping)));
      store.getDataSet().addTimedGroupingLineItemsByField(CANONICAL_NAME_FIELD)
      
      if (variant === "global-inputs") {
        Object.keys(lineItemNamesMapping).forEach((valueKey) => {
          let queryResult = store.query(StoreQuery.byName(lineItemNamesMapping[valueKey]));
          let aggregatedValue = queryResult.rowValues(lineItemNamesMapping[valueKey]);
          // let tableValues = queryResult.toSimpleTableValues();
          if (!aggregatedValue || aggregatedValue.length === 0) {
            error(`No values found from query data-source for ${lineItemNamesMapping[valueKey]}`);
          } else {
            inputs[valueKey] = {
              valueKey,
              value: [aggregatedValue]
            }
          }
        })
        // @TODO: Remove this as this is adhoc for JLL Demo
        inputs["model-start-date"] = {
          valueKey: "model-start-date",
          value: [[Intl.DateTimeFormat('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
          }).format(store.timeIndex.startDate)]],
        }
        // Method 3: For business/financial calculations (more precise)
        const getBusinessMonthDiff = (date1: Date, date2: Date): number => {
          const years = differenceInYears(date2, date1);
          const months = differenceInMonths(date2, date1) % 12;
          const days = differenceInDays(date2, date1) % 30;
          
          return Math.ceil((years * 12) + months + (days / 30));
        };
        inputs["months-in-model"] = {
          valueKey: "months-in-model",
          value: [[getBusinessMonthDiff(store.timeIndex.startDate, store.timeIndex.endDate)]]
        }
      } else if (variant === "project-version-inputs") {
        for (let pv of projectVersions) {
          const key = pv.projectId + "-" + pv.versionId;
          const storeId = getStoreId(pv.projectId, pv.versionId);
          Object.keys(lineItemNamesMapping).forEach((valueKey) => {
            let queryResult = store.query(StoreQuery.byNames([lineItemNamesMapping[valueKey]], true).and(StoreQuery.byField('store_sourceid', storeId)));
            if(queryResult.rows.length === 0) {
              error(`No values found from query data-source for project version ${key} ${lineItemNamesMapping[valueKey]}`);
            }
            let valueForProjectVersion = queryResult.rowValues(queryResult.rowLineItemName(queryResult.rows[0]));
            // if (!valueForProjectVersion || valueForProjectVersion.length === 0) {
            // } else {
              if (!inputs[key]) {
                inputs[key] = {};
              }
              (inputs as WhatIfScenarioValuesPerProject)[key][valueKey] = {
                valueKey,
                value: [valueForProjectVersion]
              }
            // }
          })
        }
      }
      console.info(`Updated ${variant} Inputs`, inputs);
    } catch (e: any) {
      error(`Error loading data from Query Data Source ${e.message}`);
    }
    return inputs;
  }

  const loadFromSalesforce = async () => {
    let inputs = {};
    const salesforceConfigs = await getSalesforceConfigKeys(companyId);

    for (let salesforceConfigKey of salesforceConfigs) {
      setDataSourceLoadingText(`Loading ${salesforceConfigKey}`);
      inputs = mergeDeepRight(inputs, (await loadSaleforceConfig(salesforceConfigKey)));
    }
    return inputs;
  }

  const loadFromDataSource = async () => {
    setLoadingFromDataSource(true);

    let updatedInputs = {};

    setDataSourceLoadingText(`Loading from query data source`);
    updatedInputs = mergeDeepRight(updatedInputs, (await loadFromQueryDataSource()))
    if (namespace === "KingEnergy" && variant === "project-version-inputs") {
      setDataSourceLoadingText(`Loading from Salesforce data source`);
      updatedInputs = mergeDeepRight(updatedInputs, (await loadFromSalesforce()));
    }
    if (variant === "global-inputs") {
      onDataLoaded(updatedInputs as WhatIfScenarioInputValues);
    } else if (variant === "project-version-inputs") {
      onDataLoaded(updatedInputs as WhatIfScenarioValuesPerProject);
    }

    setDataSourceLoadingText("Load from Data Source");
    setLoadingFromDataSource(false);
  }

  return <div style={{ position: "absolute", top: "0", right: "0" }}>
    {!loadingFromDataSource && <Button
      primary
      onClick={loadFromDataSource}
      disabled={loadingFromDataSource}
    >
      Load from Data Source
    </Button>}

    {loadingFromDataSource && <Loader
      size="tiny"
      position="right"
      active inline>{dataSourceLoadingText}</Loader>}
  </div>
}